import React from 'react';
import { FloatLabel } from '../../../Components';

interface ContactPartialFormProps<T> {
    values: T;
    onChange: <K extends keyof T>(field: K, value: T[K]) => void;
    onChangePhone?: (value: string, field: keyof T) => void;
    onChangeIban?: (value: string) => void;
    onChangeBic?: (value: string) => void;
    onPasteCancel?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
    onIbanCheck?: () => void;
    errors?: Partial<Record<keyof T, string>>;
    dictMailLabel: string;
    dictMailConfirmationLabel: string;
    dictPhoneLabel: string;
    dictPhoneConfirmationLabel: string;
    dictIbanCompanyLabel: string;
    dictBicCompanyLabel: string;
    dictErrorSubmit: string;
    dictMailConfirmationError: string;
    dictPhoneConfirmationError: string;
    dictIdentificationCodeError: string;
    isIbanOk?: boolean;
    isBelgianIban?: boolean;
}

const ContactPartialForm = <
    T extends {
        mail: string;
        mailConfirmation: string;
        phone: string;
        phoneConfirmation: string;
        iban: string;
        bic: string;
    },
>({
    values,
    onChange,
    onChangePhone,
    onChangeIban,
    onChangeBic,
    onPasteCancel,
    onIbanCheck,
    errors,
    dictMailLabel,
    dictMailConfirmationLabel,
    dictPhoneLabel,
    dictPhoneConfirmationLabel,
    dictIbanCompanyLabel,
    dictBicCompanyLabel,
    dictErrorSubmit,
    dictMailConfirmationError,
    dictPhoneConfirmationError,
    dictIdentificationCodeError,
    isIbanOk = true,
    isBelgianIban = true,
}: ContactPartialFormProps<T>) => {
    return (
        <div className="row">
            {/* Mail */}
            <div className="col-6">
                <FloatLabel
                    active={!!values.mail}
                    label={dictMailLabel}
                    labelId="mail"
                >
                    <input
                        type="text"
                        name="mail"
                        value={values.mail}
                        onChange={(e) => onChange('mail', e.target.value)}
                    />
                </FloatLabel>
                {errors?.mail && (
                    <div className="row">
                        <label className="alert-danger m-auto">
                            {dictErrorSubmit}
                        </label>
                    </div>
                )}
            </div>

            {/* Mail confirmation */}
            <div className="col-6">
                <FloatLabel
                    active={!!values.mailConfirmation}
                    label={dictMailConfirmationLabel}
                    labelId="mailConfirmation"
                >
                    <input
                        type="text"
                        name="mailConfirmation"
                        value={values.mailConfirmation}
                        onChange={(e) =>
                            onChange('mailConfirmation', e.target.value)
                        }
                        onPaste={onPasteCancel}
                    />
                </FloatLabel>
                {values.mail !== values.mailConfirmation &&
                    errors?.mailConfirmation && (
                        <div className="row">
                            <label className="alert-danger m-auto">
                                {dictMailConfirmationError}
                            </label>
                        </div>
                    )}
                {errors?.mailConfirmation && !values.mail && (
                    <div className="row">
                        <label className="alert-danger m-auto">
                            {dictErrorSubmit}
                        </label>
                    </div>
                )}
            </div>

            {/* Phone */}
            <div className="col-12 col-lg-6">
                <FloatLabel
                    active={!!values.phone}
                    label={dictPhoneLabel}
                    labelId="phone"
                >
                    <input
                        type="text"
                        name="phone"
                        value={values.phone}
                        onChange={(e) =>
                            onChangePhone
                                ? onChangePhone(e.target.value, 'phone')
                                : onChange('phone', e.target.value)
                        }
                    />
                </FloatLabel>
                {errors?.phone && (
                    <div className="row">
                        <label className="alert-danger m-auto">
                            {dictErrorSubmit}
                        </label>
                    </div>
                )}
            </div>

            {/* Phone confirmation */}
            <div className="col-12 col-lg-6">
                <FloatLabel
                    active={!!values.phoneConfirmation}
                    label={dictPhoneConfirmationLabel}
                    labelId="phoneConfirmation"
                >
                    <input
                        type="text"
                        name="phoneConfirmation"
                        value={values.phoneConfirmation}
                        onChange={(e) =>
                            onChangePhone
                                ? onChangePhone(
                                      e.target.value,
                                      'phoneConfirmation'
                                  )
                                : onChange('phoneConfirmation', e.target.value)
                        }
                        onPaste={onPasteCancel}
                    />
                </FloatLabel>
                {values.phone !== values.phoneConfirmation &&
                    errors?.phoneConfirmation && (
                        <div className="row">
                            <label className="alert-danger m-auto">
                                {dictPhoneConfirmationError}
                            </label>
                        </div>
                    )}
            </div>

            {/* IBAN */}
            <div className="col-7">
                <FloatLabel
                    active={!!values.iban}
                    label={dictIbanCompanyLabel}
                    labelId="iban"
                >
                    <input
                        type="text"
                        name="iban"
                        value={values.iban}
                        onChange={(e) =>
                            onChangeIban
                                ? onChangeIban(e.target.value)
                                : onChange('iban', e.target.value)
                        }
                        onBlur={onIbanCheck}
                    />
                </FloatLabel>
                {errors?.iban && (
                    <div className="row">
                        <label className="alert-danger m-auto">
                            {errors.iban}
                        </label>
                    </div>
                )}

                {!!values.iban && !isIbanOk && (
                    <div className="row">
                        <label className="alert-danger m-auto">
                            {dictIdentificationCodeError}
                        </label>
                    </div>
                )}
            </div>

            {/* BIC */}
            {!isBelgianIban && (
                <div className="col-5">
                    <FloatLabel
                        active={!!values.bic}
                        label={dictBicCompanyLabel}
                        labelId="bic"
                    >
                        <input
                            type="text"
                            name="bic"
                            value={values.bic}
                            onChange={(e) =>
                                onChangeBic
                                    ? onChangeBic(e.target.value)
                                    : onChange('bic', e.target.value)
                            }
                        />
                    </FloatLabel>
                    {errors?.bic && (
                        <div className="row">
                            <label className="alert-danger m-auto">
                                {dictErrorSubmit}
                            </label>
                        </div>
                    )}
                    {!!values.bic && values.bic.length !== 8 && (
                        <div className="row">
                            <label className="alert-danger m-auto">
                                {dictIdentificationCodeError}
                            </label>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ContactPartialForm;
